import React, { useState, useEffect } from 'react';
import './Upload.css';
import { fullSanitize, removeUrls, validateOrderText } from '../utils/sanitizer';

// Nomenclature caching feature - v2
function Upload({ token, userRole, uiProfile, onNavigate, onMatchingStart }) {
  const [uploadMode, setUploadMode] = useState('text'); // 'text' или 'file'
  const [textContent, setTextContent] = useState('');
  const [aiComment, setAiComment] = useState(''); // Single AI comment — goes to both parsing enrichment and matching selection
  const contentEditableRef = React.useRef(null);
  const [orderFile, setOrderFile] = useState(null);
  const [recognizedItems, setRecognizedItems] = useState(null); // null = ещё не распознано; [] допустимо после ручного удаления всех строк
  const [recognizingFile, setRecognizingFile] = useState(false);
  const [nomenclatureFile, setNomenclatureFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nomenclaturesLoaded, setNomenclaturesLoaded] = useState(false);
  const [lastUpload, setLastUpload] = useState(null);
  const [nomenclatureReady, setNomenclatureReady] = useState(false);
  const [useExistingNomenclature, setUseExistingNomenclature] = useState(false);
  const [regenerateEmbeddings, setRegenerateEmbeddings] = useState(false);
  const pricesEnabled = uiProfile === 'alessa';

  // Progress state for nomenclature caching
  const [cachingProgress, setCachingProgress] = useState(null);

  // Progress state for matching
  const [matchingProgress, setMatchingProgress] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const currentOrderIdRef = React.useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Проверить наличие номенклатуры и последний загруженный файл
  useEffect(() => {
    const checkNomenclature = async () => {
      try {
        // Лёгкая проверка — только EXISTS запрос в БД, не тянем все данные
        const checkResp = await fetch(`${API_URL}/api/nomenclature/check`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (checkResp.ok) {
          const checkData = await checkResp.json();
          setNomenclaturesLoaded(checkData.exists);
        }

        // Получить информацию о последнем загруженном файле
        const lastUploadResponse = await fetch(`${API_URL}/api/nomenclature/last-upload`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (lastUploadResponse.ok) {
          const lastUploadData = await lastUploadResponse.json();
          if (lastUploadData) {
            setLastUpload(lastUploadData);
            setUseExistingNomenclature(true);
            setNomenclatureFile(lastUploadData.filename);
          }
        }
      } catch (err) {
        setError('Не удалось получить сведения о номенклатуре');
      } finally {
        setNomenclatureReady(true);
      }
    };
    checkNomenclature();
  }, [token, API_URL]);

  const handleUploadNomenclature = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('regenerate_embeddings', regenerateEmbeddings ? 'true' : 'false');

    setLoading(true);
    setError('');
    setCachingProgress(null);

    try {
      const response = await fetch(`${API_URL}/api/nomenclature/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          window.location.reload();
          return;
        }
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Ошибка загрузки');
      }

      // Handle Server-Sent Events for progress
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let itemsCount = 0;
      let filename = file.name;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine === '') continue; // Skip empty lines

          if (trimmedLine.startsWith('data: ')) {
            const jsonData = trimmedLine.substring(6).trim();
            try {
              const progressData = JSON.parse(jsonData);
              setCachingProgress(progressData);

              // Extract items count for final success message
              if (progressData.total) {
                itemsCount = progressData.total;
              }

              // Handle completion
              if (progressData.status === 'completed') {
                setNomenclatureFile(filename);
                setLastUpload({
                  filename: filename,
                  items_count: itemsCount,
                  uploaded_at: new Date().toISOString()
                });
                setUseExistingNomenclature(true);
                setSuccess(`✓ Загружено ${itemsCount} позиций номенклатуры`);
                setNomenclaturesLoaded(true);
                setTimeout(() => {
                  setSuccess('');
                  setCachingProgress(null);
                }, 3000);
              }

              // Handle error
              if (progressData.status === 'error') {
                throw new Error(progressData.message);
              }
            } catch (parseError) {
              setError(`Ошибка парсинга данных: ${parseError.message}`);
              break; // Stop processing on parse error
            }
          }
        }
      }
    } catch (err) {
      setError(err.message);
      setCachingProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const isImageFile = (file) => !!file && (
    (file.type && file.type.startsWith('image/')) ||
    /\.(jpe?g|png|webp|heic|heif|bmp)$/i.test(file.name || '')
  );
  const isPdfFile = (file) => !!file && (
    file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '')
  );
  const isWordFile = (file) => !!file && (
    /\.(docx?)$/i.test(file.name || '') ||
    ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)
  );
  const fileKind = orderFile ? (isPdfFile(orderFile) ? 'pdf' : isWordFile(orderFile) ? 'word' : isImageFile(orderFile) ? 'photo' : 'excel') : null;
  const needsRecognition = ['photo', 'pdf', 'word'].includes(fileKind);

  const recognizeOrderFile = async (file) => {
    setRecognizingFile(true);
    setRecognizedItems(null);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (aiComment.trim()) formData.append('ai_comment', removeUrls(aiComment.trim()));

      const endpoint = isPdfFile(file) ? 'parse-pdf' : isWordFile(file) ? 'parse-word' : 'parse-image';
      const response = await fetch(`${API_URL}/api/orders/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Не удалось распознать файл заявки');

      setRecognizedItems((data.items || []).map(it => ({
        name: it.name || '',
        quantity: it.quantity ?? 1,
        unit: it.unit || 'шт',
        original_text: it.original_text || it.name || '',
      })));
    } catch (err) {
      setError(err.message);
      setOrderFile(null);
    } finally {
      setRecognizingFile(false);
    }
  };

  const handleSelectOrderFile = (file) => {
    if (!file) return;
    setError('');
    setRecognizedItems(null);
    const recognized = isImageFile(file) || isPdfFile(file) || isWordFile(file);
    if (!recognized && !/\.(xlsx?)$/i.test(file.name || '')) {
      setOrderFile(null);
      setError('Выберите файл Excel, PDF, Word (.doc, .docx) или изображение');
      return;
    }
    if (recognized && file.size > 15 * 1024 * 1024) {
      setOrderFile(null);
      setError('Файл слишком большой (максимум 15 МБ)');
      return;
    }
    setOrderFile(file);
    if (recognized) {
      recognizeOrderFile(file);
    }
  };

  const updateRecognizedItem = (index, field, value) => {
    setRecognizedItems(list => list.map((it, i) => i === index ? { ...it, [field]: value } : it));
  };
  const removeRecognizedItem = (index) => {
    setRecognizedItems(list => list.filter((_, i) => i !== index));
  };
  const addRecognizedItem = () => {
    setRecognizedItems(list => [...(list || []), { name: '', quantity: 1, unit: 'шт' }]);
  };

  const handleUploadOrder = async () => {
    if (!nomenclaturesLoaded) {
      setError('Сначала загрузите файл номенклатуры');
      return;
    }

    if (uploadMode === 'text' && !textContent.trim()) {
      setError('Введите данные заказа');
      return;
    }

    if (uploadMode === 'file' && !orderFile) {
      setError('Выберите файл заказа (Excel, PDF, Word или фото)');
      return;
    }

    if (uploadMode === 'file' && needsRecognition) {
      if (recognizingFile) {
        setError('Дождитесь распознавания файла');
        return;
      }
      if (!recognizedItems || recognizedItems.length === 0 || !recognizedItems.some(it => it.name.trim())) {
        setError('В списке не осталось ни одной позиции — проверьте список или выберите другой файл');
        return;
      }
    }

    // Валидация текста заказа перед отправкой
    if (uploadMode === 'text') {
      const validation = validateOrderText(textContent);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
    }

    // Валидация AI комментария
    if (aiComment.trim()) {
      const cv = validateOrderText(aiComment);
      if (!cv.valid) { setError('Комментарий для AI содержит недопустимый контент'); return; }
    }

    setLoading(true);
    setError('');

    // ВАЖНО: Очищаем старый orderId, чтобы не показывать результаты старого заказа
    localStorage.removeItem('currentOrderId');

    try {
      let response;

      if (uploadMode === 'text') {
        const formData = new FormData();

        // Финальная санитизация перед отправкой
        const sanitizedContent = fullSanitize(textContent);
        const sanitizedComment = aiComment.trim() ? removeUrls(aiComment.trim()) : '';

        formData.append('text_content', sanitizedContent);
        formData.append('nomenclature_file', nomenclatureFile || 'uploaded');
        if (sanitizedComment) {
          formData.append('ai_comment', sanitizedComment);
        }

        response = await fetch(`${API_URL}/api/orders/upload-text`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      } else if (needsRecognition) {
        // Фото/PDF уже распознано (см. recognizeOrderFile) и, возможно, поправлено
        // пользователем в таблице ниже — отправляем готовый список позиций.
        response = await fetch(`${API_URL}/api/orders/upload-items`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: recognizedItems
              .filter(it => it.name.trim())
              .map(it => ({ name: it.name.trim(), quantity: Number(it.quantity) || 1, unit: it.unit || 'шт', original_text: it.original_text })),
            nomenclature_file: nomenclatureFile || 'uploaded',
            ai_comment: aiComment.trim() ? removeUrls(aiComment.trim()) : undefined,
          }),
        });
      } else {
        const formData = new FormData();
        formData.append('file', orderFile);
        formData.append('nomenclature_file', nomenclatureFile || 'uploaded');
        if (aiComment.trim()) {
          formData.append('ai_comment', aiComment.trim());
        }

        response = await fetch(`${API_URL}/api/orders/upload-excel`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      }

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          window.location.reload();
          return;
        }
        throw new Error(data.detail || 'Ошибка загрузки');
      }

      const data = await response.json();

      // Очищаем поле ввода после успешной загрузки
      setTextContent('');
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = '';
      }
      setOrderFile(null);
      setRecognizedItems(null);

      setSuccess(`✓ Заказ загружен (${data.items_count} позиций). Идет обработка...`);

      // Небольшая задержка чтобы пользователь увидел первое сообщение
      await new Promise(resolve => setTimeout(resolve, 50));

      // Начать сопоставление (не выключаем loading)
      await startMatching(data.order_id);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleCancelMatching = async () => {
    const orderId = currentOrderIdRef.current;
    if (!orderId || isCancelling) return;
    setIsCancelling(true);
    try {
      await fetch(`${API_URL}/api/matching/cancel/${orderId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // ignore network errors
    }
  };

  const startMatching = async (orderId) => {
    currentOrderIdRef.current = orderId;
    setIsCancelling(false);
    try {
      setSuccess('🔄 Идет сопоставление. Пожалуйста, подождите...');
      setMatchingProgress({ progress: 0, total: 0, status: 'Инициализация...', percentage: 0 });

      // Запускаем сопоставление — не ждём ответа.
      // Nginx может оборвать соединение при долгом ответе (>60с),
      // но бэкенд продолжает работать. Ориентируемся только на поллинг прогресса.
      fetch(`${API_URL}/api/matching/start/${orderId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).then(response => {
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          window.location.reload();
        }
      }).catch(() => {
        // Соединение оборвалось (Nginx timeout) — бэкенд всё равно работает,
        // поллинг прогресса покажет результат когда завершится
      });

      // Ждём чтобы бэкенд успел инициализировать заказ
      await new Promise(resolve => setTimeout(resolve, 800));

      // Поллим прогресс до завершения, максимум 30 минут
      const MAX_POLL_MS = 30 * 60 * 1000;
      const startTime = Date.now();

      while (true) {
        if (Date.now() - startTime > MAX_POLL_MS) {
          throw new Error('Превышено время ожидания (30 минут). Проверьте статус заказа в истории.');
        }

        try {
          const progressResponse = await fetch(
            `${API_URL}/api/matching/progress/${orderId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (progressResponse.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            window.location.reload();
            return;
          }

          if (progressResponse.ok) {
            const progressData = await progressResponse.json();
            setMatchingProgress(progressData);

            if (progressData.status === 'Сопоставление завершено') {
              break;
            }
            if (progressData.status === 'Сопоставление прервано') {
              setSuccess(null);
              setError('Сопоставление прервано.');
              setLoading(false);
              setMatchingProgress(null);
              setIsCancelling(false);
              currentOrderIdRef.current = null;
              return;
            }
          }
        } catch (pollErr) {
          // Сетевая ошибка при поллинге — ждём и пробуем снова
        }

        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setSuccess('✓ Сопоставление завершено!');
      setLoading(false);
      setMatchingProgress(null);
      setIsCancelling(false);
      currentOrderIdRef.current = null;

      setTimeout(() => {
        onMatchingStart({ order_id: orderId });
      }, 1000);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setMatchingProgress(null);
      setIsCancelling(false);
      currentOrderIdRef.current = null;
    }
  };

  if (!nomenclatureReady) {
    return <div className="upload-container"><div className="upload-section upload-initial-loading">Загрузка…</div></div>;
  }

  return (
    <div className="upload-container">
      {pricesEnabled === false && <div className="upload-section">
        <h2>Шаг 1: Выберите номенклатуру</h2>

        {lastUpload && (
          <div className="nomenclature-choice">
            <div className="choice-option">
              <input
                type="radio"
                id="use-existing"
                name="nomenclature-choice"
                checked={useExistingNomenclature}
                onChange={() => {
                  setUseExistingNomenclature(true);
                  setNomenclatureFile(lastUpload.filename);
                }}
                disabled={loading}
              />
              <label htmlFor="use-existing">
                <strong>Использовать ранее загруженный файл</strong>
                <div className="file-info">
                  <span>📄 {lastUpload.filename}</span>
                  <span>({lastUpload.items_count} позиций)</span>
                  <span className="upload-date">
                    {new Date(lastUpload.uploaded_at).toLocaleDateString('ru-RU')}
                  </span>
                </div>
              </label>
            </div>

            {userRole === 'admin' && (
              <div className="choice-option">
                <input
                  type="radio"
                  id="upload-new"
                  name="nomenclature-choice"
                  checked={!useExistingNomenclature}
                  onChange={() => setUseExistingNomenclature(false)}
                  disabled={loading}
                />
                <label htmlFor="upload-new">
                  <strong>Загрузить новый файл</strong>
                </label>
              </div>
            )}
          </div>
        )}

        {userRole === 'admin' && (!lastUpload || !useExistingNomenclature) && (
          <div className="upload-box">
            <label htmlFor="nomenclature-file" className="file-input-label">
              <div className="file-icon">📁</div>
              <span>Выберите файл номенклатуры (Excel)</span>
            </label>
            <input
              id="nomenclature-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleUploadNomenclature}
              disabled={loading}
            />
            {nomenclatureFile && !useExistingNomenclature && (
              <p className="file-name">✓ {nomenclatureFile}</p>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', fontSize: '14px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={regenerateEmbeddings}
                onChange={(e) => setRegenerateEmbeddings(e.target.checked)}
                disabled={loading}
              />
              Перегенерировать embeddings для всех позиций
            </label>
            <p style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
              Формат: либо один столбец с названиями товаров, либо два столбца — название
              в первом и артикул во втором.
            </p>
            <p style={{ marginTop: '6px', fontSize: '13px', color: '#666' }}>
              Ведёте номенклатуру по <b>характеристикам</b> в 1С?{' '}
              <a
                href={`${API_URL}/api/nomenclature/template-with-characteristics?token=${encodeURIComponent(token)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Скачайте пример файла
              </a>{' '}
              — формат (Номенклатура / Артикул / <b>Характеристика</b>) распознаётся автоматически по заголовкам колонок.
            </p>
            <p style={{ marginTop: '6px', fontSize: '13px', color: '#666' }}>
              Чтобы выгрузить такой файл прямо из 1С, есть готовая обработка —{' '}
              <a href="/1c/ВыгрузкаНоменклатурыССХарактеристиками.epf" download>
                скачать обработку (.epf)
              </a>
              . Работает на 1С:Управление торговлей 11; на 1С:ERP и 1С:Комплексная автоматизация
              должна работать тоже (общий торговый модуль), но отдельно не тестировалась.
              Просто перетащите файл в окно 1С:Предприятие (или откройте через «Файл» → «Открыть»)
              и нажмите кнопку «Выгрузить». Если нужна не вся номенклатура, а только часть —
              перед этим укажите «Группу номенклатуры» и/или «Вид номенклатуры».
            </p>
          </div>
        )}

        {userRole !== 'admin' && !lastUpload && (
          <p style={{ color: '#888', fontStyle: 'italic' }}>
            Номенклатура ещё не загружена. Обратитесь к администратору.
          </p>
        )}
      </div>}

      <div className="upload-section">
        <h2>{pricesEnabled ? 'Загрузите заказ' : 'Шаг 2: Загрузите заказ'}</h2>

        <div className="mode-tabs">
          <button
            className={`mode-tab ${uploadMode === 'text' ? 'active' : ''}`}
            onClick={() => setUploadMode('text')}
            disabled={loading}
          >
            📝 Вставить текст
          </button>
          <button
            className={`mode-tab ${uploadMode === 'file' ? 'active' : ''}`}
            onClick={() => setUploadMode('file')}
            disabled={loading}
          >
            📁 Загрузить файл
          </button>
        </div>

        {uploadMode === 'text' ? (
          <div className="text-input">
            <label>
              Вставьте заказ (поддерживается текст, таблицы из писем, списки)
              <span style={{marginLeft: '10px', color: '#667eea', fontSize: '13px'}}>
                ✨ Поддержка HTML-форматирования и сложных форматов через AI
              </span>
            </label>

            <div
              ref={contentEditableRef}
              contentEditable={!loading}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === 'Enter') {
                  e.preventDefault();
                  if (!loading && nomenclaturesLoaded) {
                    handleUploadOrder();
                  }
                }
              }}
              onInput={(e) => {
                // Применяем санитизацию при вводе
                const rawContent = e.currentTarget.innerHTML;
                const sanitized = fullSanitize(rawContent);

                // Обновляем только если контент изменился после санитизации
                if (rawContent !== sanitized) {
                  e.currentTarget.innerHTML = sanitized;
                  // Восстанавливаем позицию курсора в конце
                  const range = document.createRange();
                  const sel = window.getSelection();
                  range.selectNodeContents(e.currentTarget);
                  range.collapse(false);
                  sel.removeAllRanges();
                  sel.addRange(range);
                }

                setTextContent(sanitized);
              }}
              onPaste={(e) => {
                e.preventDefault();

                const clipboardData = e.clipboardData || window.clipboardData;
                const htmlData = clipboardData.getData('text/html');
                const textData = clipboardData.getData('text/plain');

                let contentToInsert = '';

                // Проверяем, есть ли в HTML реальная разметка (таблицы, списки, br, параграфы).
                // Если нет — браузер просто обернул plain text в HTML без тегов переноса строк,
                // и такой HTML нужно обработать как plain text, иначе \n схлопывается в пробелы.
                const hasRealHtml = htmlData && /(<table|<tr|<td|<th|<li|<p\b|<br\b|<div|<b\b|<strong|<i\b|<em)/i.test(htmlData);

                if (hasRealHtml) {
                  contentToInsert = fullSanitize(htmlData);
                } else {
                  contentToInsert = removeUrls(textData);
                }

                // Вставляем очищенный контент
                const selection = window.getSelection();
                if (!selection.rangeCount) return;
                selection.deleteFromDocument();
                const range = selection.getRangeAt(0);

                if (hasRealHtml) {
                  const fragment = range.createContextualFragment(contentToInsert);
                  range.insertNode(fragment);
                } else {
                  // Конвертируем переносы строк в <br> чтобы они отображались в contentEditable
                  const escapedText = contentToInsert
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/\n/g, '<br>');
                  const fragment = range.createContextualFragment(escapedText);
                  range.insertNode(fragment);
                }

                // Обновляем textContent после вставки
                setTextContent(e.currentTarget.innerHTML);

                // Перемещаем курсор в конец вставленного контента
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
              }}
              data-placeholder="Вставьте таблицу из Excel, письма или введите текст заказа..."
              style={{
                width: '100%',
                minHeight: '350px',
                maxHeight: '600px',
                overflowY: 'auto',
                padding: '15px',
                fontSize: '14px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                backgroundColor: loading ? '#f5f5f5' : 'white',
                marginBottom: '10px',
                fontFamily: 'Arial, sans-serif',
                lineHeight: '1.6'
              }}
            />

            <div style={{marginTop: '15px'}}>
              <label style={{display: 'block', marginBottom: '5px'}}>
                <span style={{fontSize: '14px'}}>💬 Комментарий для AI</span>
                <span style={{marginLeft: '8px', color: '#888', fontSize: '11px', fontWeight: 'normal'}}>
                  необязательно
                </span>
              </label>
              <div style={{fontSize: '11px', color: '#888', marginBottom: '4px', lineHeight: '1.3'}}>
                Допишите недостающее в названиях (марки, комплекты) и/или предпочтения при выборе
              </div>
              <textarea
                className="ai-comment-field"
                value={aiComment}
                onChange={(e) => setAiComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.ctrlKey && e.key === 'Enter') {
                    e.preventDefault();
                    if (!loading && nomenclaturesLoaded) handleUploadOrder();
                  }
                }}
                placeholder='Напр.: ко всем сверлам добавь Р6М5К5; предпочитай шлифованные; исключай позиции с "А1"; марку стали ищи в артикуле'
                disabled={loading}
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '13px',
                  borderRadius: '5px',
                  border: '1px solid #ddd',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  minHeight: '50px',
                  maxHeight: '120px'
                }}
              />
            </div>
          </div>
        ) : (
          <div className="file-input-box">
            <label htmlFor="order-file" className="file-input-label">
              <div className="file-icon">📁</div>
              <span>Выберите файл заказа (Excel, PDF, Word или фото заявки)</span>
            </label>
            <input
              id="order-file"
              type="file"
              accept=".xlsx,.xls,.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                e.target.value = '';
                handleSelectOrderFile(file);
              }}
              disabled={loading || recognizingFile}
            />
            <p className="photo-review-hint">PDF, Word и фото до 15 МБ. После распознавания проверьте и исправьте позиции.</p>
            {orderFile && <p className="file-name">✓ {orderFile.name}</p>}

            {needsRecognition && recognizingFile && (
              <p className="photo-recognizing">
                {fileKind === 'pdf' ? '🔎 Распознаём PDF-заявку (все страницы, включая сканы)…' : fileKind === 'word' ? '🔎 Распознаём Word-заявку…' : '🔎 Распознаём фото заявки (в т.ч. рукописный текст)…'}
              </p>
            )}

            {needsRecognition && !recognizingFile && recognizedItems && (
              <div className="photo-review">
                <p className="photo-review-hint">
                  Распознано {recognizedItems.length} позиций — проверьте, особенно количество,
                  и поправьте при необходимости перед сопоставлением.
                </p>
                <table className="photo-review-table">
                  <thead>
                    <tr><th>Название</th><th>Кол-во</th><th>Ед.</th><th /></tr>
                  </thead>
                  <tbody>
                    {recognizedItems.map((item, idx) => (
                      <tr key={idx}>
                        <td>
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => updateRecognizedItem(idx, 'name', e.target.value)}
                            disabled={loading}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className="photo-review-qty"
                            value={item.quantity}
                            onChange={(e) => updateRecognizedItem(idx, 'quantity', e.target.value)}
                            disabled={loading}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="photo-review-unit"
                            value={item.unit}
                            onChange={(e) => updateRecognizedItem(idx, 'unit', e.target.value)}
                            disabled={loading}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="photo-review-remove"
                            onClick={() => removeRecognizedItem(idx)}
                            disabled={loading}
                            title="Удалить позицию"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button type="button" className="photo-review-add" onClick={addRecognizedItem} disabled={loading}>
                  + Добавить позицию
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {cachingProgress && cachingProgress.status === 'progress' && (
        <div className="caching-progress">
          <div className="progress-message">{cachingProgress.message}</div>
          {cachingProgress.status === 'progress' && (
            <>
              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{ width: `${cachingProgress.percent || 0}%` }}
                />
              </div>
              <div className="progress-stats">
                <span>{cachingProgress.current} / {cachingProgress.total} ({cachingProgress.percent}%)</span>
                <span>⚡ {cachingProgress.speed} items/sec</span>
                <span>⏱️ Осталось: {Math.floor(cachingProgress.eta / 60)}:{String(cachingProgress.eta % 60).padStart(2, '0')}</span>
              </div>
            </>
          )}
        </div>
      )}

      {matchingProgress && (
        <div className="caching-progress">
          <div className="progress-message">
            {matchingProgress.status && matchingProgress.status.startsWith('Выбор GPT')
              ? '🤖 ' + matchingProgress.status
              : '🔄 Сопоставление позиций'}
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar"
              style={{ width: `${matchingProgress.percentage || 0}%` }}
            />
          </div>
          <div className="progress-stats">
            <span>{matchingProgress.progress} / {matchingProgress.total} ({matchingProgress.percentage}%)</span>
            {!(matchingProgress.status && matchingProgress.status.startsWith('Выбор GPT')) && (
              <span className="progress-status">{matchingProgress.status}</span>
            )}
          </div>
        </div>
      )}

      {matchingProgress && (
        <button
          className="cancel-btn"
          onClick={handleCancelMatching}
          disabled={isCancelling}
        >
          {isCancelling ? 'Прерывание...' : '✕ Прервать сопоставление'}
        </button>
      )}

      <button
        className="submit-btn"
        onClick={handleUploadOrder}
        disabled={loading || !nomenclaturesLoaded || recognizingFile}
      >
        {loading ? 'Загрузка...' : recognizingFile ? 'Распознаём файл...' : '✓ Начать сопоставление'}
      </button>
    </div>
  );
}

export default Upload;
